import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { ROLE_BADGE_COLORS } from '@/types/settings';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PermissionEditor } from './shared/PermissionEditor';
import { RoleBadge } from './shared/RoleBadge';
const colorOptions = [
    'gray',
    'blue',
    'green',
    'yellow',
    'red',
    'purple',
    'pink',
    'orange',
    'cyan',
    'teal',
];
export function RoleEditorModal({ open, onOpenChange, role, cloneFrom, onSave, isLoading = false, permissionCategories, permissionDependencies, }) {
    return (_jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: open && (_jsx(RoleEditorModalContent, { role: role, cloneFrom: cloneFrom, onOpenChange: onOpenChange, onSave: onSave, isLoading: isLoading, permissionCategories: permissionCategories, permissionDependencies: permissionDependencies })) }));
}
function RoleEditorModalContent({ role, cloneFrom, onOpenChange, onSave, isLoading, permissionCategories, permissionDependencies, }) {
    const { t } = useTranslation('settings');
    const [name, setName] = useState(() => {
        if (role)
            return role.name;
        if (cloneFrom)
            return `${cloneFrom.name} (Copy)`;
        return '';
    });
    const [description, setDescription] = useState(() => {
        if (role)
            return role.description;
        if (cloneFrom)
            return cloneFrom.description;
        return '';
    });
    const [color, setColor] = useState(() => {
        if (role)
            return role.color;
        if (cloneFrom)
            return cloneFrom.color;
        return 'blue';
    });
    const [permissions, setPermissions] = useState(() => {
        if (role)
            return role.permissions;
        if (cloneFrom)
            return cloneFrom.permissions;
        return [];
    });
    const handleSave = () => {
        onSave({
            name,
            description,
            color,
            permissions,
            isSystem: false,
        });
    };
    const isEditing = !!role;
    const title = isEditing
        ? t('users.roleEditor.editTitle')
        : cloneFrom
            ? t('users.roleEditor.cloneTitle')
            : t('users.roleEditor.createTitle');
    return (_jsxs(DialogContent, { className: "flex max-h-[90dvh] max-w-2xl flex-col gap-0 overflow-hidden p-0", children: [_jsx(DialogHeader, { className: "border-border shrink-0 border-b p-6 pb-4", children: _jsx(DialogTitle, { children: title }) }), _jsx("div", { className: "min-h-0 flex-1 overflow-y-auto", children: _jsxs("div", { className: "space-y-6 p-6", children: [_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "role-name", children: t('users.roleEditor.name') }), _jsx(Input, { id: "role-name", value: name, onChange: (e) => setName(e.target.value), placeholder: t('users.roleEditor.namePlaceholder') })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "role-description", children: t('users.roleEditor.description') }), _jsx(Input, { id: "role-description", value: description, onChange: (e) => setDescription(e.target.value), placeholder: t('users.roleEditor.descriptionPlaceholder') })] })] }), _jsxs("div", { className: "space-y-3", children: [_jsx(Label, { children: t('users.roleEditor.badgeColor') }), _jsx("div", { className: "flex flex-wrap gap-3", children: colorOptions.map((c) => {
                                        const colors = ROLE_BADGE_COLORS[c];
                                        return (_jsx("button", { type: "button", onClick: () => setColor(c), className: cn('h-8 w-8 rounded-full border-2 transition-all', colors.bg.replace('/20', ''), color === c
                                                ? 'scale-110 border-white shadow-[0_0_0_2px_hsl(var(--primary))]'
                                                : 'border-transparent opacity-70 hover:opacity-100') }, c));
                                    }) }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-muted-foreground text-sm", children: t('users.roleEditor.preview') }), _jsx(RoleBadge, { name: name || t('users.roleEditor.name'), color: color })] })] }), _jsxs("div", { className: "space-y-3", children: [_jsx(Label, { children: t('users.roleEditor.permissions') }), _jsx(PermissionEditor, { enabledPermissions: permissions, onChange: setPermissions, categories: permissionCategories, dependencies: permissionDependencies, className: "border-border rounded-lg border p-4" })] })] }) }), _jsxs(DialogFooter, { className: "border-border shrink-0 border-t p-6 pt-4", children: [_jsx(Button, { variant: "outline", onClick: () => onOpenChange(false), disabled: isLoading, children: t('button.cancel', { ns: 'common' }) }), _jsx(Button, { onClick: handleSave, disabled: !name.trim(), loading: isLoading, children: isEditing ? t('users.roleEditor.saveChanges') : t('users.roleEditor.createRole') })] })] }));
}
